import { Inject, Injectable } from '@angular/core';
import { BaseHttpService } from '../../../modules/fwcore';
import { HttpClient } from '@angular/common/http';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { Observable, of, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import urlJoin from 'url-join';
import { VoicestormPosts, VoicestormWin, VoicestormLatest, VoicestormPublication } from '../models';
import { ConfigService } from '../../../../services/config.service';

@Injectable({
  providedIn: 'root'
})
export class VoicestormService extends BaseHttpService {
  private latestSuffix: string = '';
  private winsSuffix: string = '';
  private publicationsSuffix: string = '';
  private configInitialized: boolean = false;
  private configInitPromise: Promise<void> | null = null;

  constructor(protected http: HttpClient, @Inject(OKTA_AUTH) private oauth: OktaAuth, @Inject('CONFIG_SETTINGS') configSettings: any, private configService: ConfigService) {
    super(http, oauth);

    // Initialize config asynchronously
    this.initializeConfig(configSettings);
  }

  private async initializeConfig(configSettings: any): Promise<void> {
    if (this.configInitPromise) {
      return this.configInitPromise;
    }

    this.configInitPromise = (async () => {
      try {
        // Load voicestorm config
        const voicestormConfig = await this.configService.getConfig('voicestorm');
        this.baseUrl = voicestormConfig.apiUrl;
        this.apiKey = voicestormConfig.apiKey;

        // Get suffixes from appconfig (these are not encrypted)
        const genSettings = configSettings[<any>'voicestorm'];
        this.latestSuffix = genSettings[<any>'latestNewsSuffix'];
        this.winsSuffix = genSettings[<any>'latestWinsSuffix'];
        this.publicationsSuffix = genSettings[<any>'latestPublicationsSuffix'];

        this.configInitialized = true;
      } catch (error) {
        console.error('Error initializing VoicestormService config:', error);
        // Fallback to appconfig.json if available
        const genSettings = configSettings[<any>'voicestorm'];
        if (genSettings) {
          this.baseUrl = genSettings[<any>'apiUrl'] || '';
          this.apiKey = genSettings[<any>'apiKey'] || '';
          this.latestSuffix = genSettings[<any>'latestNewsSuffix'];
          this.winsSuffix = genSettings[<any>'latestWinsSuffix'];
          this.publicationsSuffix = genSettings[<any>'latestPublicationsSuffix'];
        }
        this.configInitialized = true;
      }
    })();

    return this.configInitPromise;
  }

  private ensureConfigInitialized(): Observable<void> {
    if (this.configInitialized) {
      return new Observable(observer => {
        observer.next();
        observer.complete();
      });
    }
    return from(this.configInitPromise!);
  }

  public getLatestWins(): Observable<VoicestormPosts<VoicestormWin>> {
    return this.ensureConfigInitialized().pipe(
      switchMap(() => {
        let token = this.getToken();

        if (token != null) {
          return this.getItem<VoicestormPosts<VoicestormWin>>(urlJoin(this.baseUrl, this.winsSuffix), this.getHttpBearerHeaders(token));
        }
        else {
          return of<VoicestormPosts<VoicestormWin>>();
        }
      })
    );
  }

  public getLatestNews(): Observable<VoicestormPosts<VoicestormLatest>> {
    return this.ensureConfigInitialized().pipe(
      switchMap(() => {
        let token = this.getToken();

        if (token != null) {
          try {
            return this.getItem<VoicestormPosts<VoicestormLatest>>(urlJoin(this.baseUrl, this.latestSuffix), this.getHttpBearerHeaders(token));
          }
          catch (e) {
            console.error(e);
            return of<VoicestormPosts<VoicestormLatest>>();
          }
        }
        else {
          return of<VoicestormPosts<VoicestormLatest>>();
        }
      })
    );
  }

  public getLatestPublications(): Observable<VoicestormPosts<VoicestormPublication>> {
    return this.ensureConfigInitialized().pipe(
      switchMap(() => {
        let token = this.getToken();

        if (token != null) {
          return this.getItem<VoicestormPosts<VoicestormPublication>>(urlJoin(this.baseUrl, this.publicationsSuffix), this.getHttpBearerHeaders(token));
        }
        else {
          return of<VoicestormPosts<VoicestormPublication>>();
        }
      })
    );
  }

}
